import { Heading } from "../../../../../../mads";
import { HtmlContent } from "../../../../../../shared/components/HtmlContent";
import { useI18n } from "../../../../../../shared/I18n";
import { decodeHtmlEntities } from "../../../../../../shared/util/descriptionFormat/decodeHtmlEntities";
import { ScreenDisplay } from "../../../../context/ViewJobScreenDisplay";
import { HiddenPreview } from "../../HiddenPreview/HiddenPreview";

import { colors, spacing } from "@indeed/ifl-tokens/react-native";
import React, { useMemo, useState } from "react";

export type JobDescriptionProps = {
  /**
   * The Job Description which can contain HTML elements.
   */
  jobDescription: string;
};

export const SIMPLE_JD_HTML_CLASS = "simple-job-description-html";

/** Strips HTML tags, decodes entities, and collapses whitespace — used for accessibility labels. */
export function stripHtmlForLabel(html: string): string {
  return decodeHtmlEntities(html.replace(/<[^>]*>/g, " "))
    .replace(/\s+/g, " ")
    .trim();
}

export const SIMPLE_JD_INJECTED_STYLES = `
    .${SIMPLE_JD_HTML_CLASS} p,
    .${SIMPLE_JD_HTML_CLASS} ul,
    .${SIMPLE_JD_HTML_CLASS} li,
    .${SIMPLE_JD_HTML_CLASS} dl,
    .${SIMPLE_JD_HTML_CLASS} dd,
    .${SIMPLE_JD_HTML_CLASS} h1,
    .${SIMPLE_JD_HTML_CLASS} h2,
    .${SIMPLE_JD_HTML_CLASS} h3,
    .${SIMPLE_JD_HTML_CLASS} h4,
    .${SIMPLE_JD_HTML_CLASS} h5,
    .${SIMPLE_JD_HTML_CLASS} h6 {
        font-size: inherit;
    }
`;

const PREVIEW_CHAR_LIMIT = 300;

const sharedHtmlContentProps = {
  className: SIMPLE_JD_HTML_CLASS,
  injectedStyles: SIMPLE_JD_INJECTED_STYLES,
};

/**
 * Truncates an HTML string respecting a visible text character limit,
 * preserving HTML tags and ensuring they are properly closed.
 */
function truncateHtml(html: string, charLimit = PREVIEW_CHAR_LIMIT): string {
  let accumulatedHtml = "";
  let accumulatedTextLength = 0;
  const openTags: string[] = [];

  // This regex splits the string into tokens: either an HTML tag or plain text
  const tokenRegex = /(<[^>]+>|[^<]+)/g;
  let match;

  while ((match = tokenRegex.exec(html)) !== null) {
    const token = match[0];

    if (token.startsWith("<")) {
      // It's an HTML tag
      accumulatedHtml += token;

      // Identify if it's a closing tag </tag> or an opening tag <tag>
      if (token.startsWith("</")) {
        openTags.pop(); // Close the last opened tag
      } else if (!token.endsWith("/>") && !token.includes("img") && !token.includes("br")) {
        // It's an opening tag (ignoring self-closing tags)
        const tagNameMatch = token.match(/<([a-zA-Z1-6]+)/);
        if (tagNameMatch) {
          openTags.push(tagNameMatch[1]);
        }
      }
    } else {
      // It's plain text
      const remainingChars = charLimit - accumulatedTextLength;

      if (token.length <= remainingChars) {
        accumulatedHtml += token;
        accumulatedTextLength += token.length;
      } else {
        // The token exceeds the limit, truncate it here
        accumulatedHtml += token.slice(0, remainingChars) + "...";
        accumulatedTextLength += remainingChars;
        break; // Limit reached, stop processing
      }
    }

    if (accumulatedTextLength >= charLimit) {
      break;
    }
  }

  // Crucial: Close all remaining open tags in reverse order after the cutoff
  while (openTags.length > 0) {
    const tag = openTags.pop();
    accumulatedHtml += `</${tag}>`;
  }

  // Fallback: If for some reason the output is empty, return a clean paragraph
  if (!accumulatedHtml.trim()) {
    const plainText = html.replace(/<[^>]+>/g, "").trim();
    return `<p>${plainText.slice(0, charLimit)}...</p>`;
  }

  return accumulatedHtml;
}

export const SimpleJobDescription = (props: JobDescriptionProps) => {
  const { gettext } = useI18n();
  const [isExpanded, setIsExpanded] = useState(false);
  const previewHtml = useMemo(() => truncateHtml(props.jobDescription), [props.jobDescription]);
  const previewLabel = useMemo(() => stripHtmlForLabel(previewHtml), [previewHtml]);
  const revealLabel = useMemo(
    () => stripHtmlForLabel(props.jobDescription),
    [props.jobDescription],
  );

  return (
    <>
      <Heading
        level={4}
        id="jobDescriptionTitleHeading"
        testID="vj-job-description-heading"
        sx={{
          color: colors.neutral1000,
          marginBlockEnd: spacing.sc2,
          marginBlockStart: spacing.sc4,
        }}
      >
        {gettext("Full job description")}
      </Heading>

      <ScreenDisplay.DesktopOnly>
        <HtmlContent {...sharedHtmlContentProps}>{props.jobDescription}</HtmlContent>
      </ScreenDisplay.DesktopOnly>

      <ScreenDisplay.MobileOnly>
        {/* One WebView stays mounted across show/hide; inPlaceContentUpdates swaps
            preview<->full without a reload so the toggle doesn't flash (RN-6861). */}
        <HiddenPreview
          isExpanded={isExpanded}
          onExpandedChange={setIsExpanded}
          accessibilityLabel={isExpanded ? revealLabel : previewLabel}
        >
          <HtmlContent {...sharedHtmlContentProps} inPlaceContentUpdates>
            {isExpanded ? props.jobDescription : previewHtml}
          </HtmlContent>
        </HiddenPreview>
      </ScreenDisplay.MobileOnly>
    </>
  );
};
